import Dialog from 'commons/components/Dialog'
import { useAuthStore } from 'domains/Users/Auth/store'
import useAuth from 'hooks/useAuth'
import useLocales from 'hooks/useLocales'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { useSnackbar } from 'notistack'
import { Box, Stack, Typography } from '@mui/material'
import VerifyCodeForm from 'domains/Users/Auth/containers/VerifyCodeForm'
import useGuestOtpChallenge from 'hooks/domains/Users/useGuestOtpChallenge'
import {
  isCaptchaCancellation,
  isCaptchaFailure,
} from 'helpers/captcha/contracts'

const OPEN_SEND_DELAY_MS = 1000

interface IOTPDialogProps {
  onSuccess?: Function
  onClose: Function
  open: boolean
  onRequestOTP?: Function
  onVerify?: Function
}

const OTPDialog = ({
  open,
  onSuccess,
  onClose,
  onRequestOTP,
  onVerify,
}: IOTPDialogProps) => {
  const { isReady } = useRouter()
  const { t } = useLocales()
  const { user, loginPhone } = useAuth()
  const { runGuestOtp, invalidate } = useGuestOtpChallenge()
  const { enqueueSnackbar } = useSnackbar()
  const { verificationMethod, setVerificationMethod } = useAuthStore(
    (state) => ({
      verificationMethod: state.verificationMethod,
      setVerificationMethod: state.setVerificationMethod,
    }),
  )

  const phoneWithoutCode = user?.phone.replace('+', '')

  const requestOtp = async (method) => {
    if (onRequestOTP) {
      await onRequestOTP(method)
      return
    }
    invalidate()
    await loginPhone(
      phoneWithoutCode,
      'WA',
      runGuestOtp,
      'VERIFY_KYC',
    )
  }

  // The form reports the captcha errors of its own resends; this covers the
  // send fired on open, which has no form around it to show them.
  const requestOtpOnOpen = async () => {
    try {
      await requestOtp(verificationMethod)
    } catch (error) {
      if (isCaptchaCancellation(error)) return
      if (isCaptchaFailure(error)) {
        enqueueSnackbar(t('key_captcha_verification_failed'), {
          variant: 'error',
        })
        return
      }
      throw error
    }
  }

  useEffect(() => {
    if (!open || !isReady) return
    setVerificationMethod('WA')
    const timer = setTimeout(() => {
      requestOtpOnOpen()
    }, OPEN_SEND_DELAY_MS)
    // Closing must neither send late nor leave a challenge open.
    return () => {
      clearTimeout(timer)
      invalidate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isReady])

  return (
    <Dialog
      open={open}
      header={
        <Typography
          variant="h4"
          textAlign="center"
          sx={(theme) => ({ color: theme.palette.grey[800] })}
        >
          {t('key_enter_otp_code')}
        </Typography>
      }
      body={
        <Stack
          sx={{
            padding: '14px',
          }}
          alignItems="center"
          spacing={2}
        >
          <Stack
            justifyContent="center"
            alignItems="center"
            sx={{ marginBottom: '14px' }}
          >
            <Typography
              sx={{ color: 'text.secondary', textAlign: 'center' }}
            >
              {t('key_otp_verification_desc_with_method', {
                method:
                  verificationMethod === 'WA' ? 'WhatsApp' : 'SMS',
                phone_number: user?.phone,
              })}
            </Typography>

            <Box sx={{ mt: 5, mb: 3 }}>
              <VerifyCodeForm
                inputPhoneNumber={phoneWithoutCode}
                otpAction="VERIFY_KYC"
                onSuccess={onSuccess}
                onVerify={(code) => onVerify(code)}
                onRequestOTP={(method) => requestOtp(method)}
              />
            </Box>
          </Stack>
        </Stack>
      }
      footer={<></>}
      onClose={() => onClose()}
    />
  )
}

export default OTPDialog
